/**
 * Assignments and submissions API.
 * Uses gateway client with auth/tenant headers.
 */
import { createHttpClient } from '@/api/http';
import { serviceUrls } from '@/config/serviceUrls';
import { showErrorToast } from '@/utils/errorHandler';

const client = createHttpClient(serviceUrls.learning);

/** Teacher-provided materials on an assignment (signed GCS URL and/or authenticated fetch path). */
export interface AssignmentAttachmentDTO {
  attachment_id: string;
  filename: string;
  content_type?: string | null;
  /** Direct browser URL when stored in GCS. */
  url?: string | null;
  /** When set (local dev storage), GET this path with auth and responseType blob. */
  fetch_path?: string | null;
}

/** Open attachment: new tab for signed GCS URL, or authenticated download for local storage. */
export async function openAssignmentAttachmentFile(att: AssignmentAttachmentDTO): Promise<void> {
  if (att.url) {
    window.open(att.url, '_blank', 'noopener,noreferrer');
    return;
  }
  if (att.fetch_path) {
    try {
      const res = await client.get(att.fetch_path, { responseType: 'blob' });
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data as BlobPart]);
      const u = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = u;
      a.download = att.filename || 'download';
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(u);
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to download file');
    }
    return;
  }
}

export type QuizQuestionType = 'multiple_choice' | 'true_false' | 'short_answer';

/** Student-safe quiz question (no answer key). Returned for AI-generated Quiz assignments. */
export interface StudentQuizQuestionDTO {
  id: string;
  number: number;
  type: QuizQuestionType;
  stem: string;
  choices: string[];
}

export interface AssignmentDTO {
  id: string;
  assignment_id: string;
  title: string;
  title_tamil?: string | null;
  instructions?: string;
  /** Interactive quiz questions when assignment type is Quiz from AI draft. */
  quiz_questions?: StudentQuizQuestionDTO[];
  /** Staff-only; omitted for student API responses. */
  ai_instructions?: string;
  /** Staff-only; omitted for student API responses. */
  answer_key_text?: string;
  /** Staff-only; omitted for student API responses. */
  answer_key_attachment?: AssignmentAttachmentDTO | null;
  grade: string;
  type: string;
  due_date?: string | null;
  status: string;
  created_by_user_id?: string;
  created_at: string;
  updated_at: string;
  attachments?: AssignmentAttachmentDTO[];
}

/** Create payload: no targets; use assignment_id from response for targets API */
export interface AssignmentCreatePayload {
  title: string;
  title_tamil?: string;
  instructions?: string;
  ai_instructions?: string;
  answer_key_text?: string;
  grade?: string;
  type?: string;
  due_date?: string;
}

export async function createAssignment(payload: AssignmentCreatePayload): Promise<AssignmentDTO> {
  const res = await client.post('/v1/learning/assignments', payload);
  return res.data as AssignmentDTO;
}

export async function listAssignments(params?: {
  grade?: string;
  status?: string;
  type?: string;
  q?: string;
  limit?: number;
  skip?: number;
}): Promise<{ items: AssignmentDTO[]; total: number; skip: number; limit: number }> {
  const res = await client.get('/v1/learning/assignments', { params });
  return res.data;
}

export async function getAssignment(assignmentId: string): Promise<AssignmentDTO> {
  const res = await client.get(`/v1/learning/assignments/${assignmentId}`);
  return res.data as AssignmentDTO;
}

/** Partial update payload: only sent fields are updated */
export interface AssignmentUpdatePayload {
  title?: string;
  title_tamil?: string;
  instructions?: string;
  ai_instructions?: string;
  answer_key_text?: string;
  grade?: string;
  type?: string;
  due_date?: string;
}

export async function updateAssignment(
  assignmentId: string,
  payload: AssignmentUpdatePayload
): Promise<AssignmentDTO> {
  const res = await client.patch(`/v1/learning/assignments/${assignmentId}`, payload);
  return res.data as AssignmentDTO;
}

/** Soft-delete assignment (learning-service marks status deleted and cancels targets). */
export async function deleteAssignment(assignmentId: string): Promise<void> {
  await client.delete(`/v1/learning/assignments/${assignmentId}`);
}

const formDataTransform = {
  headers: {
    'Content-Type': undefined,
  } as Record<string, string | undefined>,
  transformRequest: [(data: unknown, headers?: Record<string, string>) => {
    if (data instanceof FormData && headers) {
      delete headers['Content-Type'];
    }
    return data;
  }],
};

/** Upload a teacher resource (video, audio, image, PDF, etc.) for an assignment. */
export async function uploadAssignmentAttachment(
  assignmentId: string,
  file: File
): Promise<AssignmentAttachmentDTO> {
  const form = new FormData();
  form.append('file', file);
  const res = await client.post(
    `/v1/learning/assignments/${assignmentId}/attachments`,
    form,
    formDataTransform
  );
  return res.data as AssignmentAttachmentDTO;
}

export async function deleteAssignmentAttachment(
  assignmentId: string,
  attachmentId: string
): Promise<void> {
  await client.delete(`/v1/learning/assignments/${assignmentId}/attachments/${attachmentId}`);
}

/** Staff-only: upload or replace the single answer key file (not shown to students). */
export async function uploadAnswerKeyAttachment(
  assignmentId: string,
  file: File
): Promise<AssignmentAttachmentDTO> {
  const form = new FormData();
  form.append('file', file);
  const res = await client.post(
    `/v1/learning/assignments/${assignmentId}/answer-key-attachment`,
    form,
    formDataTransform
  );
  return res.data as AssignmentAttachmentDTO;
}

/** Staff-only: remove answer key file from assignment. */
export async function deleteAnswerKeyAttachment(assignmentId: string): Promise<void> {
  await client.delete(`/v1/learning/assignments/${assignmentId}/answer-key-attachment`);
}

/** Student shape in a target (for display) */
export interface AssignmentTargetStudentDTO {
  platform_user_id: string;
  display_name?: string | null;
}

export interface AssignmentTargetDTO {
  target_id: string;
  assignment_id: string;
  target_type: 'class' | 'students';
  class_id?: string | null;
  class_name?: string | null;
  students?: AssignmentTargetStudentDTO[];
  due_date?: string | null;
  status?: string;
}

/** Create target: no due_date (use assignment.due_date). */
export type CreateTargetPayload =
  | { target_type: 'class'; class_id: string }
  | { target_type: 'students'; students: Array<{ platform_user_id: string }> };

export async function createTarget(
  assignmentId: string,
  payload: CreateTargetPayload
): Promise<{ target_id: string }> {
  const res = await client.post(`/v1/learning/assignments/${assignmentId}/targets`, payload);
  return res.data as { target_id: string };
}

export async function listTargets(
  assignmentId: string,
  params?: { skip?: number; limit?: number }
): Promise<{ items: AssignmentTargetDTO[]; total: number; skip: number; limit: number }> {
  const res = await client.get(`/v1/learning/assignments/${assignmentId}/targets`, { params });
  return res.data;
}

/** Update target: only students list is updatable (for student targets). */
export interface UpdateTargetPayload {
  students: Array<{ platform_user_id: string }>;
}

export async function updateTarget(
  assignmentId: string,
  targetId: string,
  payload: UpdateTargetPayload
): Promise<AssignmentTargetDTO> {
  const res = await client.patch(
    `/v1/learning/assignments/${assignmentId}/targets/${targetId}`,
    payload
  );
  return res.data as AssignmentTargetDTO;
}

export async function deleteTarget(
  assignmentId: string,
  targetId: string
): Promise<void> {
  await client.delete(`/v1/learning/assignments/${assignmentId}/targets/${targetId}`);
}

/** Suggestion item from OCR metadata (corrections) */
export interface SubmissionOcrMetadataSuggestionDTO {
  tag?: string;
  original?: string;
  suggestion?: string;
  reason_tamil?: string;
  confidence?: number;
}

/** Rubric scores from OCR metadata */
export interface SubmissionOcrMetadataRubricDTO {
  overallScore?: number;
  grammar?: number;
  clarity?: number;
  vocabulary?: number;
  feedback?: string;
}

/** OCR metadata on a submission (text, suggestions, rubric) */
export interface SubmissionOcrMetadataDTO {
  text?: string | null;
  suggestions?: SubmissionOcrMetadataSuggestionDTO[] | null;
  rubric?: SubmissionOcrMetadataRubricDTO | null;
}

/** Narrow `validation_staff.rubric` (typed as unknown on `SubmissionDTO`). */
export function coerceValidationStaffRubric(
  staff: Record<string, unknown> | null | undefined
): SubmissionOcrMetadataRubricDTO | null {
  const raw = staff?.rubric;
  if (raw == null || typeof raw !== 'object') return null;
  return raw as SubmissionOcrMetadataRubricDTO;
}

/** Narrow `validation_staff.suggestions` to a typed array. */
export function coerceValidationStaffSuggestions(
  staff: Record<string, unknown> | null | undefined
): SubmissionOcrMetadataSuggestionDTO[] {
  const raw = staff?.suggestions;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (item): item is SubmissionOcrMetadataSuggestionDTO =>
      item != null && typeof item === 'object'
  );
}

/** Safe summary from grammar-service guided validation (student-visible). */
export type SubmissionValidationStudentSummaryDTO = Record<string, unknown>;

/** First human-readable string found in grammar-service `student_safe` object. */
export function pickValidationStudentMessage(
  s: SubmissionValidationStudentSummaryDTO | null | undefined
): string | null {
  if (!s || typeof s !== 'object') return null;
  for (const k of [
    'message',
    'summary',
    'student_message',
    'feedback',
    'hint',
    'student_feedback',
    'overview',
  ]) {
    const v = s[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  const guided = s['guided'];
  if (guided && typeof guided === 'object') {
    const gf = (guided as Record<string, unknown>)['feedback'];
    if (typeof gf === 'string' && gf.trim()) return gf.trim();
  }
  return null;
}

/** Numeric score from `student_safe` when grammar includes it (0–100). */
export function pickValidationStudentScore(
  s: SubmissionValidationStudentSummaryDTO | null | undefined
): number | null {
  if (!s || typeof s !== 'object') return null;
  for (const k of [
    'validation_score',
    'overall_score',
    'score',
    'suggested_score',
    'blended_score',
    'grade',
  ]) {
    const v = s[k];
    const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
    if (Number.isFinite(n)) return Math.max(0, Math.min(100, Math.round(n)));
  }
  const guided = s['guided'];
  if (guided && typeof guided === 'object') {
    const sc = (guided as Record<string, unknown>)['score'];
    const n = typeof sc === 'number' ? sc : typeof sc === 'string' ? Number(sc) : NaN;
    if (Number.isFinite(n)) return Math.max(0, Math.min(100, Math.round(n)));
  }
  return null;
}

export interface SubmissionDTO {
  id: string;
  submission_id: string;
  assignment_id: string;
  student_id: string;
  /** When API or client provides a display name for the submitting student (not always present). */
  student_display_name?: string | null;
  filename?: string | null;
  text_response?: string | null;
  status: string;
  grade?: number | null;
  ocr_score?: number | null;
  ocr_text?: string | null;
  feedback?: string | null;
  reviewed_by_user_id?: string | null;
  reviewed_at?: string | null;
  submitted_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  assignment_title?: string;
  /** Grade level (e.g. "1", "2") when from grouped response */
  grade_level?: string | null;
  /** URL of the submitted image (worksheet/photo) */
  image_url?: string | null;
  /** OCR result metadata: extracted text, suggestions, rubric scores */
  ocr_metadata?: SubmissionOcrMetadataDTO | null;
  /** When OCR failed, short reason from learning-service (for teachers). */
  ocr_error?: string | null;
  /** pending | completed | failed | skipped — grammar assignment validation */
  validation_status?: string | null;
  /** Short, student-safe payload from grammar-service (omit staff-only keys). */
  validation_student_summary?: SubmissionValidationStudentSummaryDTO | null;
  /** Full staff validation payload (teachers / admins only). */
  validation_staff?: Record<string, unknown> | null;
  /** When validation failed or skipped (teachers / admins only). */
  validation_error?: string | null;
  validated_at?: string | null;
  /** After publish with correction overlay (when returned by API). */
  correction_image_url?: string | null;
  /** Optional teacher review blob from API (shape varies). */
  teacher_review?: unknown;
}

export async function submitAssignment(payload: {
  assignment_id: string;
  text_response?: string;
  file?: File;
}): Promise<{ submission_id: string; status: string; message: string }> {
  const form = new FormData();
  form.append('assignment_id', payload.assignment_id);
  form.append('text_response', payload.text_response || '');
  if (payload.file) {
    form.append('file', payload.file);
  }
  // Must not set Content-Type so browser sets multipart/form-data with boundary.
  // Axios client defaults to application/json which breaks FormData parsing (422).
  const res = await client.post('/v1/learning/submissions', form, {
    headers: {
      'Content-Type': undefined,
    } as Record<string, string | undefined>,
    transformRequest: [(data: unknown, headers?: Record<string, string>) => {
      if (data instanceof FormData && headers) {
        delete headers['Content-Type'];
      }
      return data;
    }],
  });
  return res.data;
}

export async function listSubmissions(params?: {
  assignment_id?: string;
  student_id?: string;
  status?: string;
  limit?: number;
  skip?: number;
}): Promise<{ items: SubmissionDTO[]; total: number; skip: number; limit: number }> {
  const res = await client.get('/v1/learning/submissions', { params });
  return res.data;
}

export async function getSubmission(submissionId: string): Promise<SubmissionDTO> {
  const res = await client.get(`/v1/learning/submissions/${submissionId}`);
  return res.data as SubmissionDTO;
}

export async function listRecentSubmissionsGrouped(limit?: number): Promise<{
  by_grade: Record<string, SubmissionDTO[]>;
  items: SubmissionDTO[];
}> {
  const res = await client.get('/v1/learning/submissions/recent', {
    params: limit ? { limit } : undefined,
  });
  return res.data;
}

export async function reviewSubmission(
  submissionId: string,
  payload: { grade: number; feedback?: string; status?: string }
): Promise<SubmissionDTO> {
  const res = await client.post(`/v1/learning/submissions/${submissionId}/review`, payload);
  return res.data as SubmissionDTO;
}

export async function publishReview(
  submissionId: string,
  payload: { grade: number; feedback?: string }
): Promise<SubmissionDTO> {
  const res = await client.post(`/v1/learning/submissions/${submissionId}/publish`, payload);
  return res.data as SubmissionDTO;
}

/** Multipart publish with teacher correction image overlay. */
export async function publishReviewWithCorrection(
  submissionId: string,
  payload: {
    grade: number;
    feedback: string;
    file: File;
    annotationPayload?: unknown;
  }
): Promise<SubmissionDTO> {
  const form = new FormData();
  form.append('file', payload.file);
  form.append('grade', String(payload.grade));
  form.append('feedback', payload.feedback ?? '');
  if (payload.annotationPayload != null) {
    form.append('annotation_payload', JSON.stringify(payload.annotationPayload));
  }
  const res = await client.post(
    `/v1/learning/submissions/${submissionId}/publish-with-correction`,
    form,
    formDataTransform
  );
  return res.data as SubmissionDTO;
}
